import { useTranslation } from 'react-i18next';

import { NOTIF_TYPES } from '@private/notifications';
import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import api from 'api';
import { SHARED_LINK_KEYS } from 'api/sharedLink/constants';
import { ISharedLink } from 'api/sharedLink/types';
import { STATUSES } from 'constants/common';
import { notificationService } from 'utils/notificationService';
import { IBaseResponse, QueryError } from 'utils/types';

import { normalizeQueryKey } from '../utils';

export const useGetSharedLink = (shortLink: string): UseQueryResult<ISharedLink> => {
  const queryFn = () => api.sharedLink.getSharedLink(shortLink);
  return useQuery({
    queryFn,
    queryKey: normalizeQueryKey([SHARED_LINK_KEYS.SHARED_LINK, shortLink]),
  });
};

export const useCreateShortLink = (): UseMutationResult<
  IBaseResponse<string>,
  QueryError,
  ISharedLink
> => {
  const { t } = useTranslation();

  const prefix = 'common.filters.notifications.shortLink';
  const toastId = 'createShortLink';

  return useMutation({
    mutationFn: api.sharedLink.createShortLink,
    onError: (error: QueryError) => {
      const isSavingError = error?.response?.status === STATUSES.INVALID_DATA;
      const errorsExists = Object.keys(error?.response?.data?.fieldMessages || {}).length;

      if (errorsExists) {
        notificationService.show({
          dismissIn: 5000,
          message:
            error?.response?.data?.message ||
            (isSavingError ? t('notifications.invalidData') : t(`${prefix}.error`)),
          title: t('notifications.errorTitle'),
          type: NOTIF_TYPES.ERROR,
          updateId: toastId,
        });
      } else {
        notificationService.remove(toastId || '');
      }
    },
    onMutate: () => {
      notificationService.show({
        message: t(`${prefix}.loading`),
        title: t('notifications.loadingTitle'),
        toastId,
        type: NOTIF_TYPES.LOADING,
      });
    },
    onSuccess: async () => {
      notificationService.show({
        message: t(`${prefix}.success`),
        title: t('notifications.successTitle'),
        type: NOTIF_TYPES.SUCCESS,
        updateId: toastId,
      });
    },
  });
};
